import { Injectable } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { GetImpersonationTokenRequest, GetTokenRequest, UserIAMApiService, UserIdentifier } from '@vendasta/iam';
import {
  CreateDigitalAgentRequest,
  DigitalAgentSdkService,
  Permissions,
  UpdateDigitalAgentRequest,
} from '@vendasta/task-manager';
import { ApiError } from '../../api-service/api.service';
import { ConciergeHostService } from '../core/concierge-host.service';
import { DigitalAgent, DigitalAgentInterface } from './DigitalAgent';

export const PERMISSIONS_UPDATED = 'permissionsUpdated';
export const UNASSIGNED = new DigitalAgent({
  subjectId: 'Unassigned',
  userId: 'Unassigned',
  email: 'Unassigned',
  partnerId: '',
  accessiblePartnerIds: [],
});

export class ConciergeUserNotification {
  type: string;
  message: string;
  code: number;
  isError: boolean;

  constructor(notification: ApiError | { message: string; code: number }, type: string, isError = false) {
    this.type = type;
    this.message = notification.message;
    this.code = notification.code;
    this.isError = isError;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ConciergeUserService {
  private users$$: BehaviorSubject<DigitalAgent[]> = new BehaviorSubject([]);
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private assignees$: Observable<DigitalAgent[]>;
  private selectedUser$$: BehaviorSubject<DigitalAgent> = new BehaviorSubject<DigitalAgent>(null);
  private agentMap$$: BehaviorSubject<Map<string, DigitalAgent>> = new BehaviorSubject<Map<string, DigitalAgent>>(
    new Map<string, DigitalAgent>(),
  );

  constructor(
    private host: ConciergeHostService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private userIAMApiService: UserIAMApiService,
    private digitalAgentApiService: DigitalAgentSdkService,
  ) {
    this.assignees$ = this.users.pipe(
      map((agents: DigitalAgent[]) => {
        const newAgents = Object.assign([], agents);
        newAgents.unshift(UNASSIGNED);
        return newAgents;
      }),
      shareReplay(1),
    );
    this.getAgentMap();
  }

  public loadUsers(partnerId: string, onlySameNamespace = true): void {
    this.digitalAgentApiService
      .listAssigneesForPartner(partnerId, onlySameNamespace)
      .pipe(
        map((agents) => agents.map((apiAgent) => DigitalAgent.fromApi(apiAgent))),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe(
        (response) => {
          const users = response.filter((user) => user.email !== UNASSIGNED.email);
          this.users$$.next(users);
          return this.users$$.asObservable();
        },
        () => {
          this.snackbarService.errorSnack(this.translateService.instant('CONCIERGE.USERS.LOAD_ERROR'));
        },
      );
  }

  get users(): Observable<DigitalAgent[]> {
    return this.users$$.asObservable();
  }

  get assignees(): Observable<DigitalAgent[]> {
    return this.assignees$;
  }

  get selectedUser$(): Observable<DigitalAgent> {
    return this.selectedUser$$.asObservable();
  }

  set selectedUser(user: DigitalAgent) {
    this.selectedUser$$.next(user);
  }

  get agentMap(): Observable<Map<string, DigitalAgent>> {
    return this.agentMap$$.asObservable();
  }

  getAgentMap(): void {
    this.assignees
      .pipe(
        catchError(() => {
          this.snackbarService.errorSnack(this.translateService.instant('CONCIERGE.USERS.ASSIGNEES.LOAD_ERROR'));
          return of([]);
        }),
        startWith([]),
        map((agents: DigitalAgent[]): Map<string, DigitalAgent> => {
          const agentMap = new Map<string, DigitalAgent>();
          if (agents) {
            agents.forEach((agent) => agentMap.set(agent.subjectId, agent));
          }
          return agentMap;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
      .subscribe(this.agentMap$$);
  }

  get loading(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  public createUser(
    partnerId: string,
    email: string,
    firstName: string,
    lastName: string,
    sendWelcome: boolean,
    isManager: boolean,
  ): void {
    const req = new CreateDigitalAgentRequest({
      partnerId: partnerId,
      agentEmail: email,
      firstName: firstName,
      lastName: lastName,
      sendWelcome: sendWelcome,
      partnerPermissions: {
        [partnerId]: { isManager: isManager, canAccess: true },
      },
    });
    this.loading$$.next(true);
    this.digitalAgentApiService
      .createDigitalAgent(req)
      .pipe(finalize(() => this.loading$$.next(false)))
      .subscribe(
        (response) => {
          const allUsers = this.users$$.getValue();
          const newAgent = DigitalAgent.fromApi(response);
          allUsers.push(newAgent);
          allUsers.sort();
          this.users$$.next(allUsers);
          this.snackbarService.successSnack(this.translateService.instant('CONCIERGE.USERS.CREATE.SUCCESS_MESSAGE'));
        },
        () => {
          this.snackbarService.errorSnack(this.translateService.instant('CONCIERGE.USERS.CREATE.ERROR_MESSAGE'));
        },
      );
  }

  public createUserWithoutAlert(
    partnerId: string,
    email: string,
    firstName: string,
    lastName: string,
    sendWelcome: boolean,
    isManager: boolean,
  ): Observable<boolean> {
    const req = new CreateDigitalAgentRequest({
      partnerId: partnerId,
      agentEmail: email,
      firstName: firstName,
      lastName: lastName,
      sendWelcome: sendWelcome,
      partnerPermissions: {
        [partnerId]: { isManager: isManager, canAccess: true },
      },
    });
    return this.digitalAgentApiService.createDigitalAgent(req).pipe(
      map((response) => {
        return response?.subjectId !== '';
      }),
    );
  }

  public revokeUserWithoutAlert(partnerId: string, agent: DigitalAgent): Observable<boolean> {
    const permissions = this.buildPermissions(partnerId, false, false);
    const req = new UpdateDigitalAgentRequest({
      partnerId: agent.partnerId,
      agentEmail: agent.email,
      subjectId: agent.subjectId,
      firstName: agent.firstName,
      lastName: agent.lastName,
      partnerPermissions: permissions,
    });
    return this.digitalAgentApiService.updateDigitalAgent(req).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  public revokeUser(partnerId: string, agent: DigitalAgent): void {
    const permissions = this.buildPermissions(partnerId, false, false);
    const req = new UpdateDigitalAgentRequest({
      partnerId: agent.partnerId,
      agentEmail: agent.email,
      subjectId: agent.subjectId,
      firstName: agent.firstName,
      lastName: agent.lastName,
      partnerPermissions: permissions,
    });
    this.digitalAgentApiService
      .updateDigitalAgent(req)
      .pipe(
        tap(() => {
          this.loading$$.next(false);
        }),
      )
      .subscribe(
        () => {
          this.users$$.next(this.users$$.getValue().filter((currentAgent) => agent.email !== currentAgent.email));
          this.snackbarService.successSnack(this.translateService.instant('CONCIERGE.USERS.DELETE.SUCCESS_MESSAGE'));
        },
        () => {
          this.snackbarService.errorSnack(this.translateService.instant('CONCIERGE.USERS.DELETE.ERROR_MESSAGE'));
        },
      );
  }

  public getLoginUrl(partnerId: string): string {
    return this.host.conciergeHost + `/${partnerId}/login/`;
  }

  public getUser(partnerId: string, subjectId: string): Observable<DigitalAgent> {
    return this.digitalAgentApiService
      .getDigitalAgentsBySubjectIds(partnerId, [subjectId])
      .pipe(map((agents) => (agents?.length > 0 ? DigitalAgent.fromApi(agents[0]) : UNASSIGNED)));
  }

  public getMultiUsers(partnerId: string, subjectIds: string[]): Observable<DigitalAgent[]> {
    return this.digitalAgentApiService
      .getDigitalAgentsBySubjectIds(partnerId, subjectIds)
      .pipe(map((agents) => agents.map((agent) => DigitalAgent.fromApi(agent))));
  }

  public editUser(
    partnerId: string,
    user: DigitalAgentInterface,
    sendResetEmail: boolean,
    isManager: boolean,
  ): Observable<any> {
    const partnerPermissions = this.buildPermissions(partnerId, true, isManager);
    const req = new UpdateDigitalAgentRequest({
      partnerId: partnerId,
      agentEmail: user.email,
      subjectId: user.subjectId,
      firstName: user.firstName,
      lastName: user.lastName,
      sendResetEmail: sendResetEmail,
      partnerPermissions: partnerPermissions,
    });
    return this.digitalAgentApiService.updateDigitalAgent(req);
  }

  public resendWelcomeEmail(partnerId: string, subjectId: string): void {
    this.digitalAgentApiService
      .sendWelcomeEmailBySubjectId(partnerId, subjectId)
      .pipe(take(1))
      .subscribe(
        () =>
          this.snackbarService.successSnack(
            this.translateService.instant('CONCIERGE.USERS.WELCOME_EMAIL.SUCCESS_MESSAGE'),
          ),
        () =>
          this.snackbarService.errorSnack(this.translateService.instant('CONCIERGE.USERS.WELCOME_EMAIL.ERROR_MESSAGE')),
      );
  }

  public resendWelcomeEmailWithoutAlert(
    partnerId: string,
    subjectId: string,
    usePlatformDefaultDomain: boolean,
  ): Observable<any> {
    return this.digitalAgentApiService
      .sendWelcomeEmailBySubjectId(partnerId, subjectId, usePlatformDefaultDomain)
      .pipe(take(1));
  }

  public getDisplayName(subjectId: string): string {
    const user = this.agentMap$$.getValue().get(subjectId);
    if (!user) {
      return '';
    }
    let displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (displayName === '') {
      displayName = user.email;
    }
    return displayName;
  }

  public impersonate(subjectId: string): void {
    this.userIAMApiService
      .getToken(new GetTokenRequest())
      .pipe(
        switchMap((tokenResp) => {
          const impersonator: UserIdentifier = new UserIdentifier({
            token: tokenResp.token,
          });
          const impersonatee: UserIdentifier = new UserIdentifier({
            subjectId,
          });
          return this.userIAMApiService.getImpersonationToken(
            new GetImpersonationTokenRequest({
              impersonator,
              impersonatee,
            }),
          );
        }),
        take(1),
      )
      .subscribe((getImpersonationTokenResp) => {
        const url = `${this.host.conciergeHost}?session_id=${getImpersonationTokenResp.token}`;
        window.open(url, '_blank', 'noopener');
      });
  }

  private buildPermissions(partnerId: string, canAccess: boolean, isManager?: boolean): { [key: string]: Permissions } {
    // Build permissions object to the specified partner
    // The permissions will be merged with the existing permissions
    const permissions: { [key: string]: Permissions } = {};
    permissions[partnerId] = new Permissions({ canAccess, isManager });
    return permissions;
  }
}
